/* eslint-disable react/jsx-no-comment-textnodes */
import * as React from 'react';
import { Page } from '../../../cookbook-ui/Page_SC';
import { _TextWeightsOptions } from '@inmoment/the-kitchen';
import { TypographyTile } from '../../../cookbook-ui/visual-components/TypographyTile/TypographyTile';
import { IBMExampleWrapper } from './Typography_SC';
import { TitleText } from '../../../cookbook-ui/custom-components/TitleText/TitleText';
import { ALPHABET } from '../../../utils/copy';
import { CodeBlock } from '../../../cookbook-ui/custom-components/CodeBlock/CodeBlock';
import { Table } from '../../../cookbook-ui/custom-components/Table/Table';

export interface Props {}

export const Typography: React.FC<Props> = (props) => {
  const exampleTexts = _TextWeightsOptions.map((weight) => {
    return (
      <TypographyTile key={weight} stretch flexDirection="column" alignItems="flex-start">
        <p className={`kxi-text-600 kxi-text-${weight}`}>IBM Plex Sans</p>
        <p className={`kxi-text-large kxi-text-${weight}`}>{weight}</p>
        <br />
        <p className={`kxi-text-medium kxi-text${weight}`}>{ALPHABET}</p>
      </TypographyTile>
    );
  });
  return (
    <Page>
      <div>
        <TitleText
          headerText="Typography"
          footerText="Here find XI text standards for font, font weights, sizes, line height and colors."
          isLargeHeader
        />
        <hr id="IBM-Plex-Sans" />
        <br />
        <br />
        <p style={{ fontSize: 76 }} className="kxi-text-600">
          IBM Plex Sans
        </p>
        <br />
        <br />
        <IBMExampleWrapper>{exampleTexts}</IBMExampleWrapper>
        <br id="Accessing-Font" />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <p className="kxi-text-normal">
          The Text component has been removed in v14 in favor of using css classes. Here you will find an organized chart of what new classes you'll
          need to apply to get the same effect.
        </p>
        <hr />
        <br />
        <br />
        <div>
          <p className="kxi-text-medium">Previous Code</p>
          <br />
          <CodeBlock
            code={`// React:
<p className="kxi-text-normal">...</p>

// Angular:
<xi-text [size]="1">...</xi-text>`}
          />
          <br />
          <br />
          <p className="kxi-text-medium">New Code</p>
          <br />
          <CodeBlock
            code={`// React:
<div className={'kxi-text-normal'}>...</div>

// Angular:
<div class="kxi-text-normal">...</div>`}
          />
        </div>
        <br />
        <br />
        <Table
          columns={['Old v13 Property: size', 'Old v13 CSS Class', 'New v14 CSS Class', 'Size in rem', 'Size in px']}
          rows={[
            ["'small'", 'text-size-small', 'kxi-text-xsmall', '0.75rem', '12px'],
            ["'body'", 'text-size-body', 'kxi-text-small', '0.875rem', '14px'],
            ['1', 'text-size-1', 'kxi-text-normal', '1rem', '16px'],
            ['2', 'text-size-2', 'kxi-text-medium', '1.125rem', '18px'],
            ['3', 'text-size-3', 'kxi-text-large', '1.375rem', '22px'],
            ['6', 'text-size-6', 'kxi-text-xlarge', '2rem', '32px'],
            ['8', 'text-size-8', 'kxi-text-xxlarge', '2.625rem', '42px'],
          ]}
        />
        <br />
        <p className="kxi-text-normal">All sizes not documented here have been removed.</p>
        <br />
        <br />
        <Table
          columns={['Old v13 Property: weight', 'Old v13 CSS Class', 'New v14 CSS Class']}
          rows={[
            ['300', 'text-300', 'kxi-text-300'],
            ["'light'", 'text-light', 'kxi-text-300'],
            ['400', 'text-400', 'kxi-text-400'],
            ["'normal'", 'text-normal', 'kxi-text-400'],
            ['600', 'text-600', 'kxi-text-600'],
            ["'semi-bold'", 'text-semi-bold', 'kxi-text-600'],
          ]}
        />
        <br />
        <br />
        <Table
          columns={['Old v13 Property: align', 'Old v13 CSS Class', 'New v14 CSS Class']}
          rows={[
            ["'left'", 'text-left', 'kxi-text-left'],
            ["'right'", 'text-right', 'kxi-text-right'],
            ["'center'", 'text-center', 'kxi-text-center'],
          ]}
        />
        <br />
        <br />
        <Table
          columns={['Old v13 Property: wrap', 'Old v13 CSS Class', 'New v14 CSS Class']}
          rows={[
            ["'normal'", 'text-wrap-normal', 'kxi-text-wrap-normal'],
            ["'inherit'", 'text-wrap-inherit', 'kxi-text-wrap-inherit'],
            ["'pre'", 'text-wrap-pre', 'kxi-text-wrap-pre'],
            ["'-moz-initial'", 'text-wrap--moz-initial', 'kxi-text-wrap--moz-initial'],
            ["'initial'", 'text-wrap-initial', 'kxi-text-wrap-initial'],
            ["'revert'", 'text-wrap-revert', 'kxi-text-wrap-revert'],
            ["'unset'", 'text-wrap-unset', 'kxi-text-wrap-unset'],
            ["'nowrap'", 'text-wrap-nowrap', 'kxi-text-wrap-nowrap'],
            ["'-moz-pre-wrap'", 'text-wrap--moz-pre-wrap', 'kxi-text-wrap--moz-pre-wrap'],
            ["'break-spaces'", 'text-wrap-break-spaces', 'kxi-text-wrap-break-spaces'],
            ["'pre-line'", 'text-wrap-pre-line', 'kxi-text-wrap-pre-line'],
            ["'pre-wrap'", 'text-wrap-pre-wrap', 'kxi-text-wrap-pre-wrap'],
          ]}
        />
        <br />
        <br />
        <br />
        <p className="kxi-text-large">Manipulating Text Color</p>
        <br />
        <p className="kxi-text-normal">
          Changing the text color works through css variables now. The color will always be set to orca on normal contrast. On high contrast, it will
          inherit the font-color from its color class. To get any font color other than the color primaries, you'll have to create a custom css class
          that either changes the font color manually, or the preferred method of changing the css variable --font-color.
        </p>
        <br />
        <p className="kxi-text-medium">Example</p>
        <br />
        <CodeBlock
          code={`// Will be orca text because that's the default.
<div class="kxi-text-normal">subtitle</div>

// Will be admin text because the div is high contrast.
<div class="admin kxi-high-contrast kxi-text-normal">subtitle</div>

// Text will still be orca as it is normal contrast.
<div class="admin kxi-text-normal">text</div>`}
        />
        <br />
        <br />
        <br />
      </div>
    </Page>
  );
};
